import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { Product } from "@/graphql/codegen/graphql"
import { useMergedIncotermsLocations } from "@/hooks/useMergedIncotermsLocations"
import { NotificationType } from "@/schemas/notification"
import { AddSpecialPriceForm } from "@/screens/Pricing/components/forms/AddSpecialPriceForm"
import { usePriceChange } from "@/screens/Pricing/hooks/qraphql/usePriceChange"
import type { SpecialPriceSchemaFormValues } from "@/screens/Pricing/schema/SpecialPriceSchema"
import { SpecialPriceSchema } from "@/screens/Pricing/schema/SpecialPriceSchema"
import { useGetIncotermsQuery } from "@/screens/Procurement/PurchaseOrder/DeliveryTermsAndShippingLegs/hooks/deliveryTerms/useGetIncotermsQuery"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface AddSpecialPriceDialogProps {
  onClose: () => void
  onSuccess: () => void
  selectedProduct: Product
}

const AddSpecialPriceDialog: FC<AddSpecialPriceDialogProps> = ({ onClose, onSuccess, selectedProduct }) => {
  const { data: incotermsData } = useGetIncotermsQuery()
  const { locations } = useMergedIncotermsLocations(selectedProduct?.locationsAssociations ?? [])
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { addSpecialPricePackage } = usePriceChange()

  const methods = useForm({
    defaultValues: {
      customerId: null,
      shipFrom: null,
      incoterms: null,
      incotermsLocation: null,
    },
    mode: "onChange",
    resolver: zodResolver(SpecialPriceSchema),
  })

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState<boolean>(false)

  const {
    formState: { isDirty, isValid },
  } = methods

  const confirmCancel = () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSuccess = async ({
    customerId,
    shipFrom,
    incoterms,
    incotermsLocation,
  }: SpecialPriceSchemaFormValues) => {
    const { error } = await addSpecialPricePackage({
      input: {
        productId: selectedProduct.productId,
        companyId: customerId as string,
        shipFromWarehouseAreaId: shipFrom as string,
        incoterms: incoterms as string,
        incotermsLocationId: incotermsLocation as string,
      },
    })
    const hasError = error?.message

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to add special price.",
      })
      return
    }
    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Special price successfully added.",
    })
    onSuccess()
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth>
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>Add Special Price Package</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <AddSpecialPriceForm locations={locations} incotermsData={incotermsData} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={!isValid || !isDirty} onClick={methods.handleSubmit(handleSuccess)}>
              Add Package
            </Button>
          </DialogActions>
        </>
      </Dialog>
    </>
  )
}

export default AddSpecialPriceDialog
